/**
 * Format receipt data as plain text and send to the Local Print Agent.
 */

import { convertDate24Hrs } from 'helpers/formatDate';
import { amtStr } from 'helpers/numFormatter';
import { printRaw, printText } from './localPrintAgent';
import { PaymentClass } from 'generated/graphql';

/** ESC-POS commands */
const ESC = 0x1b;
const GS = 0x1d;
const ESC_INIT = [ESC, 0x40];
const ESC_ALIGN_LEFT = [ESC, 0x61, 0x00];
const ESC_ALIGN_CENTER = [ESC, 0x61, 0x01];
const ESC_ALIGN_RIGHT = [ESC, 0x61, 0x02];
const ESC_BOLD_ON = [ESC, 0x45, 0x01];
const ESC_BOLD_OFF = [ESC, 0x45, 0x00];
const LF = 0x0a;
const RECEIPT_WIDTH = 32;
const ORDER_RECEIPT_WIDTH = 42; // Match Bixolon OrderReceipt LINE_CHARS

function appendBytes(buf: number[], bytes: number[]): void {
	buf.push(...bytes);
}

function appendText(buf: number[], text: string): void {
	for (let i = 0; i < text.length; i++) {
		const c = text.charCodeAt(i);
		buf.push(c > 255 ? 0x3f : c);
	}
}

function padLine(
	left: string,
	right: string,
	width: number = RECEIPT_WIDTH,
): string {
	const pad = width - left.length - right.length;
	return pad > 0 ? left + ' '.repeat(pad) + right : left + ' ' + right;
}

function bytesToBase64(bytes: number[]): string {
	let binary = '';
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return typeof btoa !== 'undefined'
		? btoa(binary)
		: Buffer.from(bytes).toString('base64');
}

/** Raster image for ESC-POS GS v 0: 1 bit per dot, 8 dots per byte, row-major */
export interface EscPosRaster {
	data: number[];
	widthBytes: number;
	height: number;
}

/** Optional logo (top) and/or QR (before cut) rasters for bill receipt */
export interface ReceiptRasters {
	logoRaster?: EscPosRaster | null;
	qrRaster?: EscPosRaster | null;
}

/**
 * Build raw ESC-POS receipt bytes (base64) with left, center, and right alignment.
 * Optionally add logo at top (logoRaster) and/or e-Invoice QR before the cut (qrRaster).
 */
export function buildRawReceiptEscPos(
	lines?: {
		center?: string[];
		left?: string[];
		right?: string[];
		items?: Array<
			| { left: string; right: string; bold?: boolean }
			| { center: string; bold?: boolean }
			| { insertQrPlaceholder?: boolean }
		>;
		width?: number;
	},
	rasters?: ReceiptRasters | EscPosRaster | null,
): string {
	// Support legacy second arg as qrRaster only
	const logoRaster =
		rasters && typeof rasters === 'object' && 'logoRaster' in rasters
			? (rasters as ReceiptRasters).logoRaster
			: null;
	const qrRaster =
		rasters && typeof rasters === 'object'
			? 'qrRaster' in rasters
				? (rasters as ReceiptRasters).qrRaster
				: (rasters as EscPosRaster).data
				? (rasters as EscPosRaster)
				: null
			: null;

	const buf: number[] = [];
	appendBytes(buf, ESC_INIT);

	// Optional: logo at top (outlet logo or OutletIcon fallback)
	if (
		logoRaster?.data?.length &&
		logoRaster.widthBytes > 0 &&
		logoRaster.height > 0
	) {
		appendBytes(buf, ESC_ALIGN_CENTER);
		const xL = logoRaster.widthBytes % 256;
		const xH = Math.floor(logoRaster.widthBytes / 256);
		const yL = logoRaster.height % 256;
		const yH = Math.floor(logoRaster.height / 256);
		appendBytes(buf, [GS, 0x76, 0x30, 0x00, xL, xH, yL, yH]);
		for (const byte of logoRaster.data) buf.push(byte & 0xff);
		buf.push(LF, LF);
	}

	const defaultLines = {
		center: ['MenuX', '--- Test Receipt ---', ''],
		left: ['Order #001', 'Table A1', '------------------------'],
		items: [
			{ left: 'Item 1 x 2', right: 'RM 20.00' },
			{ left: 'Item 2 x 1', right: 'RM 15.00' },
		],
		right: ['------------------------', 'Total: RM 35.00', 'Thank you!'],
		width: RECEIPT_WIDTH,
	};
	const { center, left, items, right, width = RECEIPT_WIDTH } = {
		...defaultLines,
		...lines,
	};

	// Center-aligned header (skip if empty)
	if (center?.length) {
		appendBytes(buf, ESC_ALIGN_CENTER);
		for (const line of center) {
			appendText(buf, line);
			buf.push(LF);
		}
	}

	// Left-aligned order info (skip if empty)
	if (left?.length) {
		appendBytes(buf, ESC_ALIGN_LEFT);
		for (const line of left) {
			appendText(buf, line);
			buf.push(LF);
		}
	}

	// Item lines: left+right (padded), center-aligned, or QR placeholder
	for (const item of items ?? []) {
		if (
			'insertQrPlaceholder' in item &&
			item.insertQrPlaceholder &&
			qrRaster?.data?.length &&
			qrRaster.widthBytes > 0 &&
			qrRaster.height > 0
		) {
			appendBytes(buf, ESC_ALIGN_CENTER);
			buf.push(LF, LF);
			const xL = qrRaster.widthBytes % 256;
			const xH = Math.floor(qrRaster.widthBytes / 256);
			const yL = qrRaster.height % 256;
			const yH = Math.floor(qrRaster.height / 256);
			appendBytes(buf, [GS, 0x76, 0x30, 0x00, xL, xH, yL, yH]);
			for (const byte of qrRaster.data) buf.push(byte & 0xff);
			buf.push(LF, LF);
			continue;
		}
		const bold = 'bold' in item && item.bold;
		if (bold) appendBytes(buf, ESC_BOLD_ON);
		if ('center' in item && item.center !== undefined) {
			appendBytes(buf, ESC_ALIGN_CENTER);
			appendText(buf, item.center);
			appendBytes(buf, ESC_ALIGN_LEFT); // reset for next lines
		} else if ('left' in item) {
			appendBytes(buf, ESC_ALIGN_LEFT);
			appendText(buf, padLine(item.left, item.right ?? '', width));
		}
		if (bold) appendBytes(buf, ESC_BOLD_OFF);
		buf.push(LF);
	}

	// Right-aligned totals (skip if empty)
	if (right?.length) {
		appendBytes(buf, ESC_ALIGN_RIGHT);
		for (const line of right) {
			appendText(buf, line);
			buf.push(LF);
		}
	}

	// Feed 2 extra lines before cut
	buf.push(LF, LF, LF, LF);
	appendBytes(buf, [GS, 0x56, 0x00]);

	return bytesToBase64(buf);
}

/**
 * Load image from URL and convert to 1-bit raster for ESC-POS GS v 0.
 * Returns { data, widthBytes, height } or null on failure (e.g. CORS).
 * Scales image to fit within maxWidth x maxHeight (scales up small images e.g. OutletIcon SVG).
 * @param url - Image URL (same-origin or CORS-enabled)
 * @param maxWidth - Max width in pixels (default 200)
 * @param maxHeight - Max height in pixels (default same as maxWidth)
 */
export async function imageUrlToEscPosRaster(
	url: string,
	maxWidth: number = 200,
	maxHeight?: number,
): Promise<EscPosRaster | null> {
	if (typeof window === 'undefined' || !url) return null;
	const capH = maxHeight ?? maxWidth;
	return new Promise(resolve => {
		const img = new window.Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => {
			try {
				const canvas = document.createElement('canvas');
				let w = img.width;
				let h = img.height;
				// Scale to fit within maxWidth x maxHeight (scale UP small images like OutletIcon SVG)
				if (w > 0 && h > 0) {
					const scale = Math.min(maxWidth / w, capH / h);
					w = Math.round(w * scale);
					h = Math.round(h * scale);
				}
				// ESC-POS raster width must be multiple of 8; allow up to 608px for large logos
				w = Math.min(608, Math.floor(w / 8) * 8) || 8;
				h = Math.min(608, h);
				canvas.width = w;
				canvas.height = h;
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					resolve(null);
					return;
				}
				ctx.drawImage(img, 0, 0, w, h);
				const imageData = ctx.getImageData(0, 0, w, h);
				const data = imageData.data;
				const widthBytes = Math.ceil(w / 8);
				const raster: number[] = [];
				for (let y = 0; y < h; y++) {
					for (let byteCol = 0; byteCol < widthBytes; byteCol++) {
						let byte = 0;
						for (let bit = 0; bit < 8; bit++) {
							const px = byteCol * 8 + bit;
							if (px < w) {
								const i = (y * w + px) * 4;
								const luminance =
									(data[i] ?? 0) * 0.299 +
									(data[i + 1] ?? 0) * 0.587 +
									(data[i + 2] ?? 0) * 0.114;
								if (luminance < 128) byte |= 0x80 >> bit;
							}
						}
						raster.push(byte);
					}
				}
				resolve({ data: raster, widthBytes, height: h });
			} catch {
				resolve(null);
			}
		};
		img.onerror = () => resolve(null);
		img.src = url;
	});
}

/** Default timeout for raw receipt print (seconds); overrides agent 15s to avoid "Raw print timeout (15s)". */
const DEFAULT_RAW_RECEIPT_TIMEOUT_SECONDS = 30;

/**
 * Send raw ESC-POS formatted receipt (left/center/right) to the local print agent.
 * Optional rasters: { logoRaster } for logo at top, { qrRaster } for QR before cut.
 * Uses timeoutSeconds (default 30) so agent does not use its 15s default.
 */
export async function printRawReceiptViaAgent(
	printerName?: string,
	lines?: Parameters<typeof buildRawReceiptEscPos>[0],
	rasters?: ReceiptRasters | EscPosRaster | null,
	opts?: { timeoutSeconds?: number; timeoutMs?: number },
): Promise<void> {
	const base64 = buildRawReceiptEscPos(lines, rasters);
	const timeout =
		opts?.timeoutSeconds != null
			? { timeoutSeconds: opts.timeoutSeconds }
			: opts?.timeoutMs != null
			? { timeoutMs: opts.timeoutMs }
			: { timeoutSeconds: DEFAULT_RAW_RECEIPT_TIMEOUT_SECONDS };
	await printRaw(base64, printerName, timeout);
}

export interface OrderReceiptRawInput {
	outletName?: string;
	orderData?: {
		docNo?: string;
		pax?: number;
		createdTs?: string;
		patronName?: string;
		[k: string]: any;
	};
	tableData?: { prefix?: string; tableNumber?: string };
	orderItemData?: Array<{
		quantity?: number;
		menuItem?: {
			name?: string;
			kitchenName?: string;
			isOpenItem?: boolean;
			kitchenPrinter?: { name?: string };
		};
		openItemName?: string;
		specialRequest?: string;
		orderItemOption?: Array<{
			orderItemOptionItem?: Array<{ optionItem?: { name?: string } }>;
		}>;
		orderItemSetMenuOption?: Array<{
			orderItemSetMenuOptionItem?: Array<{
				setMenuOptionItem?: { menuItem?: { kitchenName?: string } };
			}>;
		}>;
		isTakeaway?: boolean;
		[k: string]: any;
	}>;
	orderMode?: string;
	voidQuantity?: number;
	reasonCode?: string;
	remark?: string;
	printerName?: string;
	receiptType?: 'order-receipt' | 'guest-order-receipt';
	/** Receipt width in chars per line (alignment). Default 42. */
	width?: number;
}

function formatOrderReceiptRawLines(
	input: OrderReceiptRawInput,
): Parameters<typeof buildRawReceiptEscPos>[0] {
	const {
		outletName = '',
		orderData = {},
		tableData,
		orderItemData = [],
		orderMode = 'confirm',
		voidQuantity = 0,
		reasonCode = '',
		remark = '',
		receiptType = 'order-receipt',
		width: inputWidth,
	} = input;

	const loggedInUser =
		typeof localStorage !== 'undefined'
			? JSON.parse(localStorage.getItem('loggedInUser') || '{}')
			: {};
	const dateStr = orderData?.createdTs
		? convertDate24Hrs(orderData.createdTs)
		: convertDate24Hrs(new Date().toISOString());

	const tableStr = tableData
		? `${tableData.prefix ?? ''}${tableData.tableNumber ?? ''}`
		: '';

	if (receiptType === 'guest-order-receipt') {
		const w = inputWidth ?? ORDER_RECEIPT_WIDTH;
		const sep = '-'.repeat(w);
		const items: Array<
			| { left: string; right: string; bold?: boolean }
			| { center: string; bold?: boolean }
		> = [];

		if (orderMode === 'void') {
			items.push({ center: 'Void Item', bold: true });
			items.push({ center: '', bold: false });
		}

		// Table (bold)
		items.push({ left: `Table: ${tableStr}`, right: '', bold: true });
		// Visitor | Pax (bold)
		items.push({
			left: `Visitor: ${orderData?.patronName ?? '-'}`,
			right: `Pax: ${orderData?.pax ?? '-'}`,
			bold: true,
		});
		// Order No | Date (bold)
		items.push({
			left: `Order No: ${orderData?.docNo ?? ''}`,
			right: dateStr,
			bold: true,
		});
		// Separator
		items.push({ left: sep, right: '', bold: true });
		// Description | Unit (bold)
		items.push({ left: 'Description', right: 'Unit', bold: true });
		items.push({ left: '-----------', right: '----', bold: false });

		let sumQty = 0;
		for (const oi of orderItemData) {
			const name = oi?.menuItem?.isOpenItem
				? oi?.openItemName
				: oi?.menuItem?.kitchenName ?? oi?.menuItem?.name ?? 'Item';
			const qty = oi?.quantity ?? 0;
			sumQty += qty;
			const suffix =
				orderMode === 'void'
					? oi?.isTakeaway
						? `(T) -${voidQuantity}`
						: `-${voidQuantity}`
					: oi?.isTakeaway
					? `(T) [ ] ${qty}`
					: `[ ] ${qty}`;
			items.push({ left: name ?? 'Item', right: suffix });
			if (oi?.specialRequest)
				items.push({ left: oi.specialRequest, right: '' });
			for (const opt of oi?.orderItemOption ?? []) {
				for (const oii of opt?.orderItemOptionItem ?? []) {
					items.push({ left: `  ${oii?.optionItem?.name ?? ''}`, right: '' });
				}
			}
			for (const setOpt of oi?.orderItemSetMenuOption ?? []) {
				for (const smoi of setOpt?.orderItemSetMenuOptionItem ?? []) {
					items.push({
						left: `  ${smoi?.setMenuOptionItem?.menuItem?.kitchenName ?? ''}`,
						right: '',
					});
				}
			}
		}
		if (orderMode === 'void') {
			if (reasonCode) items.push({ left: `Reason: ${reasonCode}`, right: '' });
			if (remark) items.push({ left: `Remark: ${remark}`, right: '' });
		}

		// Separator
		items.push({ left: sep, right: '', bold: false });
		// For confirm: sumtotal, ----- (right-aligned)
		if (orderMode === 'confirm') {
			items.push({ left: '', right: `${sumQty}`, bold: false });
			items.push({ left: '', right: '-----', bold: false });
		}
		// Remark
		items.push({
			left: `Remark: ${loggedInUser?.name ?? ''}`,
			right: '',
			bold: false,
		});

		return { center: [], left: [], items, right: [], width: w };
	}

	// order-receipt (kitchen slip) - match printReceiptText/OrderReceipt layout from image
	const w = inputWidth ?? ORDER_RECEIPT_WIDTH;
	const separatorLine = '-'.repeat(w);

	const printerNames =
		[
			...new Set(
				orderItemData
					.map(x => x?.menuItem?.kitchenPrinter?.name)
					.filter(Boolean),
			),
		].join(', ') || 'KitchenPrinter';

	const items: Array<
		| { left: string; right: string; bold?: boolean }
		| { center: string; bold?: boolean }
	> = [];
	if (orderMode === 'void') {
		items.push({ center: 'Void Item', bold: true });
		items.push({ center: '', bold: false });
	}
	items.push(
		// Line 1: Outlet | Cover (bold like printReceiptText)
		{
			left: `Outlet: ${outletName || 'MenuX'}`,
			right: `Cover: ${orderData?.pax ?? '-'}`,
			bold: true,
		},
		// Line 2: Order No | Table (bold)
		{
			left: `Order No: ${orderData?.docNo ?? ''}`,
			right: `Table: ${tableStr || '-'}`,
			bold: true,
		},
		// Line 3: Printer name (bold)
		{ left: printerNames, right: '', bold: true },
		// Line 4: Staff name | Date (bold)
		{ left: loggedInUser?.name ?? '', right: dateStr, bold: true },
		// Separator (bold) - length based on width
		{ left: separatorLine, right: '', bold: true },
	);

	for (const oi of orderItemData) {
		const name = oi?.menuItem?.isOpenItem
			? oi?.openItemName
			: oi?.menuItem?.kitchenName ?? oi?.menuItem?.name ?? 'Item';
		const qty = orderMode === 'void' ? voidQuantity : oi?.quantity ?? 0;
		const suffix = oi?.isTakeaway ? `(T) x${qty}` : `x${qty}`;
		items.push({ left: name ?? 'Item', right: suffix }); // items not bold
		if (oi?.specialRequest) items.push({ left: oi.specialRequest, right: '' });
		for (const opt of oi?.orderItemOption ?? []) {
			for (const oii of opt?.orderItemOptionItem ?? []) {
				items.push({ left: `  ${oii?.optionItem?.name ?? ''}`, right: '' });
			}
		}
		for (const setOpt of oi?.orderItemSetMenuOption ?? []) {
			for (const smoi of setOpt?.orderItemSetMenuOptionItem ?? []) {
				items.push({
					left: `  ${smoi?.setMenuOptionItem?.menuItem?.kitchenName ?? ''}`,
					right: '',
				});
			}
		}
	}

	if (orderMode === 'void') {
		if (reasonCode) items.push({ left: `Reason: ${reasonCode}`, right: '' });
		if (remark) items.push({ left: `Remark: ${remark}`, right: '' });
	}

	items.push({ left: separatorLine, right: '', bold: true });

	return {
		center: [],
		left: [],
		items,
		right: [],
		width: inputWidth ?? ORDER_RECEIPT_WIDTH,
	};
}

/**
 * Print order receipt as raw ESC-POS (left/center/right) via Local Print Agent.
 * Similar to printReceiptText for order-receipt and guest-order-receipt, but uses raw format.
 * Does NOT overwrite or replace printReceiptText (Bixolon).
 * @param input - Receipt data. Use input.width for printer alignment (chars per line, default 42).
 */
export async function printOrderReceiptRawViaLocalAgent(
	input: OrderReceiptRawInput,
): Promise<void> {
	const { printerName, ...rest } = input;
	const lines = formatOrderReceiptRawLines({
		...rest,
		receiptType: input.receiptType ?? 'order-receipt',
		width: input.width,
	});
	await printRawReceiptViaAgent(printerName, lines);
}

/** Match Bixolon BillReceipt LINE_CHARS */
const BILL_RECEIPT_WIDTH = 42;

/** Input for new-order (QR table slip) raw receipt via Local Print Agent */
export interface NewOrderRawInput {
	outletName?: string;
	tableNo?: string;
	userName?: string;
	outletID?: string;
	qrID?: string;
	accountID?: string;
	/** Printer IP for network printer (deviceIP) */
	printerName?: string;
	/** Receipt width in chars (deviceAlignmentCode). Default 42. */
	width?: number;
	/** Optional QR image URL or data URL to embed on slip */
	qrImageUrl?: string;
}

function formatNewOrderRawLines(
	input: NewOrderRawInput,
): Parameters<typeof buildRawReceiptEscPos>[0] {
	const {
		outletName = '',
		tableNo = '',
		userName = '',
		width: inputWidth,
	} = input;
	const w = inputWidth ?? ORDER_RECEIPT_WIDTH;
	const sep = '-'.repeat(Math.min(w, 42));
	const printedOn = new Date();
	const dateStr = convertDate24Hrs(printedOn.toISOString());

	const center: string[] = [];
	center.push(`Welcome to ${outletName}`);
	center.push('');
	center.push(sep);

	const items: Array<
		| { left: string; right: string; bold?: boolean }
		| { center: string; bold?: boolean }
		| { insertQrPlaceholder?: boolean }
	> = [];
	items.push({ left: `Table: ${tableNo}`, right: '', bold: true });
	items.push({ left: `Printed on: ${dateStr}`, right: '', bold: true });
	items.push({ left: `Staff: ${userName}`, right: '', bold: true });
	items.push({ left: sep, right: '', bold: false });
	// QR placeholder: only rendered when qrRaster is passed to buildRawReceiptEscPos
	items.push({ insertQrPlaceholder: true });
	items.push({ center: 'Scan the QR to Order', bold: true });
	items.push({ center: '', bold: false });

	return {
		center,
		left: [],
		items,
		right: [],
		width: w,
	};
}

/**
 * Print new-order (QR table slip) as raw ESC-POS via Local Print Agent (network printer).
 * Same content as printReceiptText receiptType 'new-order', for connectType Network when isSDK is false.
 */
export async function printNewOrderRawViaLocalAgent(
	input: NewOrderRawInput,
): Promise<void> {
	const { printerName, qrImageUrl, ...rest } = input;
	const lines = formatNewOrderRawLines({ ...rest, width: input.width });

	let qrRaster: EscPosRaster | null = null;
	if (qrImageUrl) {
		qrRaster =
			(await imageUrlToEscPosRaster(qrImageUrl, 200).catch(() => null)) ?? null;
	}

	const rasters: ReceiptRasters = {};
	if (qrRaster) rasters.qrRaster = qrRaster;
	await printRawReceiptViaAgent(
		printerName,
		lines,
		Object.keys(rasters).length ? rasters : undefined,
	);
}

function toProperCase(s: string): string {
	if (!s) return s;
	return s.replace(
		/\w\S*/g,
		t => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase(),
	);
}

function paymentClassLabel(
	paymentClass?: string,
	creditCardType?: string,
): string {
	if (!paymentClass) return 'Payment';
	switch (paymentClass) {
		case 'UNSETTLED_BILL':
			return 'Unsettled Bill';
		case 'CREDIT':
			switch (creditCardType) {
				case 'VISA_CARD':
					return 'VISA';
				case 'MASTERCARD':
					return 'MASTERCARD';
				case 'AMERICAN_EXPRESS_CARD':
					return 'AMERICAN EXPRESS';
				case 'DINERS_CARD':
					return 'DINERS';
				case 'JCB_CARD':
					return 'JCB';
				case 'UNIONPAY_CARD':
					return 'UNIONPAY';
				case 'DEBIT_CARD':
					return 'DEBIT';
				default:
					return 'Credit';
			}
		case 'ONLINE':
			return 'Online Payment';
		case 'NON_GUEST':
			return 'Non Guest';
		case 'SUSPEND_FOLIO':
			return 'Suspend Folio';
		case 'BANK_TT':
			return 'Bank TT';
		case 'GRAB_FOOD':
			return 'Grab Food';
		default:
			return toProperCase(paymentClass.replace(/_/g, ' '));
	}
}

export interface BillReceiptRawInput {
	outlet?: {
		name?: string;
		contactNo?: string;
		company?: {
			name?: string;
			companyRegNo?: string;
			tin?: string;
			sstNo?: string;
			address?: {
				address?: string;
				postCode?: string;
				city?: string;
				state?: string;
			};
		};
		address?: {
			address?: string;
			postCode?: string;
			city?: string;
			state?: string;
		};
		[k: string]: any;
	};
	outletName?: string;
	/** Page/mode: bill-settlement | bill-printing | void-payment | void-bill | digital-document */
	mode?: string;
	bill?: {
		billNo?: string;
		refID?: string;
		docNo?: string;
		createdTs?: string;
		patron?: { patronClass?: string };
		order?: {
			docNo?: string;
			pax?: number;
			table?: { prefix?: string; tableNumber?: string };
			[k: string]: any;
		};
		table?: { prefix?: string; tableNumber?: string };
		pax?: number;
		modBy?: string;
		createdBy?: string;
		orderDiscount?: Array<{ status?: string; discountID?: string }>;
		taxSchemeFooterInfo?: Array<{ name?: string; rate?: string; sum?: number }>;
		[k: string]: any;
	};
	orderItems?: Array<{
		ID?: string;
		quantity?: number;
		amount?: number;
		menuItem?: {
			name?: string;
			kitchenName?: string;
			menuItemOptions?: Array<{
				optionID?: string;
				option?: { isAddToPrice?: boolean; name?: string };
			}>;
		};
		specialRequest: string;
		openItemName?: string;
		isTakeaway?: boolean;
		orderItemTax?: Array<{ taxAmount?: number }>;
		orderItemOption?: Array<{
			orderItemID?: string;
			optionID?: string;
			orderItemOptionItem?: Array<{
				basePrice?: number;
				price?: number;
				optionItem?: { name?: string };
				orderItemOptionItemTax?: Array<{ taxAmount?: number }>;
			}>;
		}>;
		orderItemSetMenuOption?: Array<{
			orderItemSetMenuOptionItem?: Array<{
				setMenuOptionItem?: { menuItem?: { name?: string } };
			}>;
		}>;
		orderItemDiscount?: Array<{
			status?: string;
			discountID?: string;
			baseAmount?: number;
		}>;
		orderItemDiscountTax?: Array<{ taxAmount?: number }>;
		[k: string]: any;
	}>;
	negOrderItem?: Record<
		string,
		{
			quantity?: number;
			amount?: number;
			taxAmount?: number;
			orderItemDiscBaseAmount?: number;
			orderItemDiscTaxAmount?: number;
			[k: string]: any;
		}
	>;
	getDiscount?: Array<{ ID?: string; code?: string }>;
	totals?: {
		discount?: number;
		subtotal?: number;
		serviceCharge?: number;
		tax?: number;
		total?: number;
		rounding?: number;
	};
	latestPayment?: Array<{
		paymentClass?: string;
		creditCardType?: string;
		amount?: number;
		receivedAmount?: number;
		roundingAmount?: number;
		changeAmount?: number;
		[k: string]: any;
	}>;
	getTaxSchemeDetail?: Array<{
		taxType?: string;
		taxRate?: number;
		taxCode?: string;
	}>;
	getTaxSummary?: Array<{ code?: string; amount?: number; taxAmount?: number }>;
	getUsersByAccountAndSoftware?: Array<{ ID?: string; name?: string }>;
	officer?: Array<{ name?: string; designation?: string }>;
	staff?: Array<{ name?: string; designation?: string }>;
	currencyCode?: string;
	patronName?: string;
	/** Outlet logo URL, or fallback default (e.g. OutletIcon). Used for logo at top of receipt. */
	logoUrl?: string;
	printerName?: string;
	width?: number;
}

function formatBillReceiptRawLines(
	input: BillReceiptRawInput,
): Parameters<typeof buildRawReceiptEscPos>[0] {
	const {
		outlet = {},
		outletName = '',
		mode = 'bill-printing',
		bill = {},
		orderItems = [],
		negOrderItem = {},
		getDiscount = [],
		totals = {},
		latestPayment = [],
		getTaxSchemeDetail = [],
		getTaxSummary = [],
		getUsersByAccountAndSoftware = [],
		officer = [],
		staff = [],
		currencyCode = '',
		patronName = '',
		width: inputWidth,
	} = input;
	const w = inputWidth ?? BILL_RECEIPT_WIDTH;
	const sep = '-'.repeat(w);
	const order = bill?.order ?? {};
	const company = outlet?.company ?? {};
	const addr = company?.address ?? outlet?.address ?? {};
	const table = bill?.table ?? order?.table;
	const tableStr = table
		? `${table.prefix ?? ''}${table.tableNumber ?? ''}`.trim()
		: '';
	const pax = bill?.pax ?? order?.pax ?? 0;
	const createdTs = bill?.createdTs ?? '';
	const dateStr = createdTs
		? convertDate24Hrs(createdTs)
		: convertDate24Hrs(new Date().toISOString());
	const patronClass =
		bill?.patron?.patronClass ?? bill?.order?.patron?.patronClass ?? 'Visitor';
	const cashierId = bill?.modBy ?? bill?.createdBy;
	const cashier = getUsersByAccountAndSoftware?.find(u => u?.ID === cashierId);
	const cashierName = cashier?.name ?? '';

	const isSettlement = mode === 'bill-settlement';
	const billNo = bill?.billNo ?? '';
	const docNo = order?.docNo ?? bill?.docNo ?? bill?.refID ?? '';

	// Center: company header (match BillReceipt)
	const center: string[] = [];
	center.push(company?.name ?? outlet?.name ?? (outletName || 'Bill Receipt'));
	center.push(`(Company Reg No: ${company?.companyRegNo ?? 'NA'})`);
	center.push(`(Company Tin No: ${company?.tin ?? 'NA'})`);
	center.push(
		`(SST Reg No: ${company?.sstNo ?? outlet?.taxRegistrationNo ?? 'NA'})`,
	);
	center.push(addr?.address ?? '');
	center.push(
		[addr?.postCode, addr?.city, addr?.state].filter(Boolean).join(' ') +
			(addr?.postCode || addr?.city || addr?.state ? '.' : ''),
	);
	center.push(outlet?.contactNo ?? '');
	center.push('');
	center.push(outlet?.name ?? outletName);

	// Items: left-right lines (match alignment() in BillReceipt)
	const items: Array<
		| { left: string; right: string; bold?: boolean }
		| { center: string; bold?: boolean }
	> = [];

	// Bill No / Order No + date (bold)
	if (mode !== 'bill-settlement') {
		items.push({
			left: `Bill No: ${billNo || docNo}`,
			right: `${dateStr}`,
			bold: true,
		});
	} else {
		items.push({ left: `Order No: ${docNo}`, right: `${dateStr}`, bold: true });
	}

	// Voided Bill
	if (mode === 'void-bill') {
		items.push({ left: 'Voided Bill', right: '', bold: true });
	} else {
		// Patron
		const patronLabel =
			patronClass === 'Staff'
				? 'Staff       '
				: patronClass === 'Officer'
				? 'Officer     '
				: patronClass === 'Visitor'
				? 'Visitor     '
				: patronClass === 'Hotel Guest'
				? 'Hotel Guest '
				: patronClass === 'Member'
				? 'Member      '
				: 'Visitor     ';
		items.push({ left: `${patronLabel}: ${patronName ?? '-'}`, right: '' });
	}

	// Table
	if (mode !== 'void-bill') {
		items.push({ left: `Table       : ${tableStr}`, right: '' });
	}

	// Cover
	if (mode !== 'void-bill' || order?.serviceMode === 'DINE_IN') {
		items.push({ left: `Cover       : ${pax}`, right: '' });
	}

	// Cashier
	if (mode !== 'void-bill') {
		items.push({ left: `Cashier     : ${cashierName}`, right: '' });
		items.push({ left: ' ', right: '' });
	}

	// Order items (match BillReceipt: name (T) xqty / name xqty + amount)
	for (const oi of orderItems) {
		const qty = oi?.quantity ?? 0;
		const negQty = negOrderItem?.[oi?.ID]?.quantity ?? 0;
		const displayQty = qty - negQty;
		if (displayQty <= 0) continue;
		let totalOITax = 0;
		oi?.orderItemTax?.forEach(x => {
			totalOITax += x?.taxAmount ?? 0;
		});
		let addToPriceOptionTotal = 0;
		oi?.orderItemOption?.forEach(oiOption => {
			oiOption?.orderItemOptionItem?.forEach(oiOptionItem => {
				const matchedOption = oi?.menuItem?.menuItemOptions?.find(
					m => m?.optionID === oiOption?.optionID,
				);
				if (matchedOption?.option?.isAddToPrice) {
					let totalTax = 0;
					oiOptionItem?.orderItemOptionItemTax?.forEach(t => {
						totalTax += t?.taxAmount ?? 0;
					});
					addToPriceOptionTotal +=
						(Number(oiOptionItem?.basePrice ?? 0) + totalTax) *
						(displayQty / (qty || 1));
				}
			});
		});
		const negAmt =
			(negOrderItem?.[oi?.ID]?.amount ?? 0) +
			(negOrderItem?.[oi?.ID]?.taxAmount ?? 0);
		const amt =
			Number(oi?.amount ?? 0) + addToPriceOptionTotal + totalOITax - negAmt;
		const name = oi?.openItemName ?? oi?.menuItem?.name ?? 'Item';
		const suffix = oi?.isTakeaway ? `(T) x${displayQty}` : `x${displayQty}`;
		const existSpecialRequest =
			oi?.specialRequest !== null &&
			oi.specialRequest !== undefined &&
			oi?.specialRequest !== ''
				? `${oi?.specialRequest}`
				: null;
		console.log('special request', oi?.specialRequest);

		items.push({
			left: `${name} ${suffix}`,
			right: `${currencyCode}${amtStr(amt)}`,
		});

		const optionNames = oi?.menuItem?.menuItemOptions?.map(mio => {
			const selected = oi?.orderItemOption?.find(
				oio => oio.optionID === mio.optionID,
			)?.orderItemOptionItem[0]?.optionItem?.name;
			console.log('selectted 12345', selected);
			return selected;
		});
		// if (mode === 'bill-settlement') {
		// 	items.push({
		// 		left: `${optionNames}`,
		// 		right: ``,
		// 	});
		// }

		// items.push({left: `${existSpecialRequest}`, right: ''});
		// Options (non add-to-price: option name + price)
		oi?.orderItemOption?.forEach(oiOption => {
			oiOption?.orderItemOptionItem?.forEach(oiOptionItem => {
				const matchedOption = oi?.menuItem?.menuItemOptions?.find(
					m => m?.optionID === oiOption?.optionID,
				);
				console.log(
					'matchOption',
					matchedOption,
					oiOptionItem?.optionItem?.name,
				);

				if (
					matchedOption?.option?.isAddToPrice === false &&
					oiOptionItem?.optionItem?.name
				) {
					let totalTax = 0;
					oiOptionItem?.orderItemOptionItemTax?.forEach(t => {
						totalTax += t?.taxAmount ?? 0;
					});
					const optAmt =
						(Number(oiOptionItem?.basePrice ?? 0) + totalTax) *
						(displayQty / (qty || 1));
					console.log(
						'orderitemoptionItemname',
						oiOptionItem?.optionItem?.name,
					);
					console.log('oiOptionItem', oiOptionItem?.optionItem?.name);

					items.push({
						left: oiOptionItem?.optionItem?.name ?? '',
						right:
							// oiOptionItem?.price === 0
							// 	? ''
							// 	: 
								`${currencyCode}${amtStr(Math.abs(optAmt))}`,
					});
					// if (mode === 'bill-settlement') {
					// 	items.push({
					// 		left: `${optionNames}`,
					// 		right: ``,
					// 	});
					// }
				}
			});
		});
		// items.push({
		// 	left: `${optionNames}`,
		// 	right: ``,
		// });
		// Set menu options
		oi?.orderItemSetMenuOption?.forEach(oiSet => {
			oiSet?.orderItemSetMenuOptionItem?.forEach(smoi => {
				items.push({
					left: `  ${smoi?.setMenuOptionItem?.menuItem?.name ?? ''}`,
					right: '',
				});
			});
		});
		// Per-item discount
		const activeOID = oi?.orderItemDiscount?.filter(
			y => String(y?.status ?? '').toUpperCase() === 'ACTIVE',
		)[0];
		if (activeOID && getDiscount?.length) {
			const discCode =
				getDiscount?.find(d => d?.ID === activeOID?.discountID)?.code ?? ' ';
			let totalOIDTax = 0;
			oi?.orderItemDiscountTax?.forEach(x => {
				totalOIDTax += x?.taxAmount ?? 0;
			});
			const oidAmt =
				(activeOID?.baseAmount ?? 0) +
				totalOIDTax -
				(negOrderItem?.[oi?.ID]?.orderItemDiscBaseAmount ?? 0) -
				(negOrderItem?.[oi?.ID]?.orderItemDiscTaxAmount ?? 0);
			items.push({
				left: `Discount: ${discCode}`,
				right: `(${currencyCode}${amtStr(oidAmt)})`,
			});
		}
	}

	// Footer (match BillReceiptFooter)
	items.push({ left: ' ', right: '' });
	const subtotal = Number(totals.subtotal ?? 0);
	const discountAmt = Number(totals.discount ?? 0);
	const rounding = Number(totals.rounding ?? 0);
	const total = Number(totals.total ?? 0);
	let roundAmount = 0;
	latestPayment?.forEach(x => {
		roundAmount += x?.roundingAmount ?? 0;
	});
	const displayRounding = isSettlement ? rounding : roundAmount;

	items.push({
		left: 'Sub-Total',
		right: `${currencyCode}${amtStr(subtotal)}`,
	});
	const activeOrderDiscount = (isSettlement
		? bill?.orderDiscount
		: bill?.order?.orderDiscount
	)?.filter(
		(y: { status?: string }) =>
			String(y?.status ?? '').toUpperCase() === 'ACTIVE',
	)[0];
	const discountCode =
		getDiscount?.find(d => d?.ID === activeOrderDiscount?.discountID)?.code ??
		' ';
	items.push({
		left: `Discount: ${discountCode}`,
		right: `(${currencyCode}${amtStr(discountAmt)})`,
	});
	if (displayRounding < 0) {
		items.push({
			left: 'Rounding',
			right: `(${currencyCode}${Math.abs(displayRounding).toFixed(2)})`,
		});
	} else {
		items.push({
			left: 'Rounding',
			right: `${currencyCode}${amtStr(displayRounding)}`,
		});
	}
	items.push({ left: ' ', right: '', bold: true });
	const totalDisplay = isSettlement
		? Math.abs(total)
		: Math.abs(total) + (roundAmount ?? 0);
	items.push({
		left: 'Total',
		right: `${currencyCode}${amtStr(totalDisplay)}`,
		bold: true,
	});
	items.push({ left: ' ', right: '', bold: true });

	// Tax breakdown
	const taxSchemeFooterInfo = isSettlement
		? bill?.taxSchemeFooterInfo
		: bill?.order?.taxSchemeFooterInfo;
	const sortedTaxScheme = [...(getTaxSchemeDetail ?? [])].sort((a, b) => {
		if ((a?.taxType ?? '') < (b?.taxType ?? '')) return -1;
		if ((a?.taxType ?? '') > (b?.taxType ?? '')) return 1;
		if ((a?.taxRate ?? 0) < (b?.taxRate ?? 0)) return -1;
		if ((a?.taxRate ?? 0) > (b?.taxRate ?? 0)) return 1;
		return 0;
	});
	const taxAmount = (taxType: string, taxRate: number) => {
		const t = taxSchemeFooterInfo
			?.filter((x: { name?: string }) => x?.name === taxType)
			?.filter(
				(v: { rate?: string }) => parseInt(v?.rate ?? '0', 10) === taxRate,
			)[0];
		return t?.sum ?? 0;
	};
	sortedTaxScheme?.forEach(x => {
		items.push({
			left: `${x?.taxCode ?? ''} (${x?.taxRate ?? 0}%)`,
			right: `${currencyCode}${amtStr(
				Number(taxAmount(x?.taxType ?? '', x?.taxRate ?? 0)),
			)}`,
		});
	});

	// Payment section (for non bill-settlement)
	if (mode !== 'void-bill' && mode !== 'bill-settlement') {
		items.push({ left: ' ', right: '', bold: true });
		items.push({ left: 'Payment', right: '', bold: true });
		latestPayment?.forEach(el => {
			const label = paymentClassLabel(el?.paymentClass, el?.creditCardType);
			const amt =
				el?.paymentClass === 'UNSETTLED_BILL' ? el?.amount : el?.receivedAmount;
			items.push({ left: label, right: `${currencyCode}${amtStr(amt ?? 0)}` });
			if (el?.paymentClass === 'CASH' && (el?.changeAmount ?? 0) > 0) {
				items.push({
					left: '  Change: ',
					right: `${currencyCode}${amtStr(el?.changeAmount ?? 0)}`,
				});
			}

			if (el?.paymentClass === PaymentClass.BankTt ) {
			items.push({ left: 'Bank Code', right: el?.paymentCode, bold: true });
			}

			if (
				el?.paymentClass === PaymentClass.BankTt ||
				el?.paymentClass === PaymentClass.Cheque ||
				el?.paymentClass === PaymentClass.Online ||
				el?.paymentClass === PaymentClass.Skipper ||
				el?.paymentClass === PaymentClass.Member
			) {
			items.push({ left: 'Remark', right: el?.remark, bold: true });
			}
			if (el?.paymentClass === PaymentClass.Voucher) {
				items.push({ left: 'Voucher Code', right: el?.paymentCode, bold: true });
			}
			if (el?.paymentClass === PaymentClass.Voucher) {
			items.push({ left: 'Remark', right: el?.referenceNo, bold: true });
			}
		});
		// SST Summary (always print section to match Bixolon)
		items.push({ left: ' ', right: '' });
		items.push({ left: 'SST Summary', right: '', bold: true });
		items.push({
			left: 'Tax Cd',
			right: 'Amt Excl SST(RM) Tax(RM)',
			bold: true,
		});
		let totalAmount = 0;
		let totalTaxAmount = 0;
		(getTaxSummary ?? []).forEach(x => {
			totalAmount += x?.amount ?? 0;
			totalTaxAmount += x?.taxAmount ?? 0;
			items.push({
				left: x?.code ?? '',
				right: `${amtStr(x?.amount ?? 0)}       ${amtStr(x?.taxAmount ?? 0)}`,
			});
		});
		items.push({
			left: 'Total',
			right: `${amtStr(totalAmount)}       ${amtStr(totalTaxAmount)}`,
			bold: true,
		});

		// QR placeholder (printed first), then e-Invoice text, then Thank you
		const company = outlet?.company ?? {};
		const qrCodeUrl =
			bill?.qrCodeUrl != null
				? typeof bill?.qrCodeUrl === 'string'
					? bill?.qrCodeUrl
					: (bill?.qrCodeUrl as any)?.url ?? (bill?.qrCodeUrl as any)
				: null;
		const uuid = bill?.einvoice_irbm_uniqueID ?? '-';
		const createdDate = bill?.createdTs
			? String(bill.createdTs).slice(0, 10)
			: '';
		const eInvStart = company?.eInvStartDate
			? String(company.eInvStartDate).slice(0, 10)
			: '';
		const eInvEnd = company?.eInvEndDate
			? String(company.eInvEndDate).slice(0, 10)
			: '';
		const eInvoiceCondition =
			bill?.is_einvoice === true &&
			company?.isEInvIntegrated === true &&
			createdDate >= eInvStart &&
			createdDate <= eInvEnd &&
			qrCodeUrl != null;
		if (eInvoiceCondition) {
			items.push({ left: ' ', right: '' });
			items.push({ insertQrPlaceholder: true });
			const eInvoice3rdParty = outlet?.integrate3rdPartyeInvoice;
			if (eInvoice3rdParty) {
				items.push({ center: 'If e-invoice is required,', bold: false });
				items.push({ center: 'please scan the QR code within', bold: false });
				items.push({
					center: 'the calendar month of your purchase',
					bold: false,
				});
			} else {
				items.push({ center: 'Scan within 72 hours if', bold: false });
				items.push({ center: 'e-Invoice is required', bold: false });
			}
			items.push({ center: `UUID : ${uuid}`, bold: false });
		}

		items.push({ left: ' ', right: '' });
		items.push({
			left: "Guest's Signature : _________________________",
			right: '',
		});
		items.push({ left: ' ', right: '' });
		items.push({
			center: 'Thank you & we hope to see you again soon.',
			bold: false,
		});
	} else {
		items.push({ left: ' ', right: '', bold: true });
		if (mode === 'bill-settlement') {
			items.push({
				left: 'Name : ___________________________________',
				right: '',
			});
			items.push({ left: ' ', right: '' });
			items.push({
				left: 'Room/ACC : _______________________________',
				right: '',
			});
			items.push({ left: ' ', right: '' });
			items.push({
				left: 'Signature : ______________________________',
				right: '',
			});
			items.push({ left: ' ', right: '' });
		} else {
			// void-bill: print Thank you
			// alignment(`Guest's Signature : ______________________`, `\n`, true);
			items.push({
				left: "Guest's Signature : _________________________",
				right: '',
			});
			items.push({ left: ' ', right: '', bold: true });
			items.push({
				center: 'Thank you & we hope to see you again soon.',
				bold: false,
			});
		}
	}

	return { center, left: [], items, right: [], width: w };
}

/**
 * Print bill receipt as raw ESC-POS via Local Print Agent (network printer).
 * Use in loadReceiptPrinter onCompleted for bill-receipt style.
 * Logo at top: use logoUrl (outlet logo or OutletIcon fallback). When e-Invoice applies, fetches QR and appends it.
 */
export async function printBillReceiptRawViaLocalAgent(
	input: BillReceiptRawInput,
): Promise<void> {
	const { printerName, bill, outlet, logoUrl, ...rest } = input;
	const lines = formatBillReceiptRawLines({
		...rest,
		bill,
		outlet,
		width: input.width,
	});

	// Logo at top: 200x200 px max (width and height)
	let logoRaster: EscPosRaster | null = null;
	if (logoUrl) {
		logoRaster =
			(await imageUrlToEscPosRaster(logoUrl, 200, 200).catch(() => null)) ??
			null;
	}

	let qrRaster: EscPosRaster | null = null;
	const company = outlet?.company ?? {};
	const qrCodeUrl =
		bill?.qrCodeUrl != null
			? typeof bill?.qrCodeUrl === 'string'
				? bill?.qrCodeUrl
				: (bill?.qrCodeUrl as any)?.url ?? (bill?.qrCodeUrl as any)
			: null;
	const createdDate = bill?.createdTs
		? String(bill.createdTs).slice(0, 10)
		: '';
	const eInvStart = company?.eInvStartDate
		? String(company.eInvStartDate).slice(0, 10)
		: '';
	const eInvEnd = company?.eInvEndDate
		? String(company.eInvEndDate).slice(0, 10)
		: '';
	const eInvoiceCondition =
		bill?.is_einvoice === true &&
		company?.isEInvIntegrated === true &&
		createdDate >= eInvStart &&
		createdDate <= eInvEnd &&
		qrCodeUrl != null;
	if (eInvoiceCondition && typeof qrCodeUrl === 'string') {
		qrRaster =
			(await imageUrlToEscPosRaster(qrCodeUrl, 200).catch(() => null)) ?? null;
	}

	const rasters: ReceiptRasters = {};
	if (logoRaster) rasters.logoRaster = logoRaster;
	if (qrRaster) rasters.qrRaster = qrRaster;
	await printRawReceiptViaAgent(
		printerName,
		lines,
		Object.keys(rasters).length ? rasters : undefined,
	);
}

export interface OrderReceiptInput {
	outletName?: string;
	order?: {
		docNo?: string;
		table?: { prefix?: string; tableNumber?: string };
		[k: string]: any;
	};
	orderItems?: Array<{
		quantity?: number;
		menuItem?: { name?: string };
		unitPrice?: string | number;
		totalPrice?: string | number;
		[key: string]: any;
	}>;
	currencyCode?: string;
}

/**
 * Format order data as plain-text receipt for the local print agent.
 */
export function formatOrderReceiptAsText(input: OrderReceiptInput): string {
	const {
		outletName = '',
		order = {},
		orderItems = [],
		currencyCode = '',
	} = input;
	const docNo = order?.docNo ?? '';
	const table = order?.table;
	const tableStr = table
		? `${table.prefix ?? ''}${table.tableNumber ?? ''}`.trim()
		: '';

	const lines: string[] = [];
	lines.push('--- Receipt ---');
	if (outletName) lines.push(outletName);
	if (docNo)
		lines.push(`Order #${docNo}${tableStr ? `  Table ${tableStr}` : ''}`);
	lines.push('---');

	let total = 0;
	for (const oi of orderItems) {
		const qty = oi?.quantity ?? 1;
		const name = oi?.menuItem?.name ?? 'Item';
		const price = Number(oi?.unitPrice ?? oi?.totalPrice ?? 0);
		const rowTotal = Number(oi?.totalPrice ?? price * qty);
		total += rowTotal;
		lines.push(`${name} x${qty}  ${currencyCode}${amtStr(rowTotal)}`);
	}
	lines.push('---');
	lines.push(`Total: ${currencyCode}${amtStr(total)}`);
	lines.push('---');
	return lines.join('\n');
}

/**
 * Format and send order receipt to the local print agent.
 * Uses default printer if printerName is omitted.
 */
export async function printOrderReceiptViaAgent(
	input: OrderReceiptInput & { printerName?: string },
): Promise<void> {
	const { printerName, ...rest } = input;
	const text = formatOrderReceiptAsText(rest);
	await printText(text, printerName);
}

/**
 * Send arbitrary text to the local print agent.
 * Alias for printText from localPrintAgent.
 */
export { printText as printViaLocalAgent } from './localPrintAgent';
